import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Check, Receipt, RotateCcw, Wallet, X } from 'lucide-react';
import { api } from '../../api';
import { pode, useSessao } from '../../sessao';
import { Modal } from '../../componentes/Modal';

interface Despesa {
  id: string; tipo: string; valor: string; em: string; descricao: string | null; status: string; km: number | null; motivo_recusa: string | null;
  decidido_em: string | null; reembolsado_em: string | null; versao: number; usuario_id: string; usuario: string; os_id: string | null; os_numero: string | null;
  placa: string | null; tem_comprovante: boolean; decidido_por: string | null;
}
const NOME_TIPO: Record<string, string> = { combustivel: 'Combustível', alimentacao: 'Alimentação', pedagio: 'Pedágio', estacionamento: 'Estacionamento', material: 'Material', hospedagem: 'Hospedagem', transporte: 'Transporte', outro: 'Outro' };
const NOME_STATUS: Record<string, [string, string]> = { lancada: ['A analisar', 's-aviso'], aprovada: ['Aprovada — a reembolsar', 's-na'], recusada: ['Recusada', 's-nok'], reembolsada: ['Reembolsada', 's-ok'] };
const FILTROS = [['lancada', 'A analisar'], ['aprovada', 'A reembolsar'], ['recusada', 'Recusadas'], ['reembolsada', 'Reembolsadas'], ['', 'Todas']] as const;
const real = (v: string | number) => Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const dataBr = (d: string) => d.split('-').reverse().join('/');

export function Despesas() {
  const { usuario } = useSessao();
  const podeAprovar = pode(usuario, 'despesas.aprovar');
  const [params, setParams] = useSearchParams();
  const filtro = params.get('status') ?? 'lancada';
  const [lista, setLista] = useState<Despesa[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [foto, setFoto] = useState<Despesa | null>(null);
  const [recusar, setRecusar] = useState<Despesa | null>(null);
  const [motivo, setMotivo] = useState('');

  const carregar = useCallback(() => {
    const q = new URLSearchParams(); if (filtro) q.set('status', filtro);
    api<Despesa[]>(`/despesas?${q}`).then(setLista).catch((e) => setErro(e.message));
  }, [filtro]);
  useEffect(() => { carregar(); }, [carregar]);
  const porTecnico = useMemo(() => {
    const m = new Map<string, { nome: string; total: number; n: number }>();
    for (const d of lista ?? []) { const x = m.get(d.usuario_id) ?? { nome: d.usuario, total: 0, n: 0 }; x.total += Number(d.valor); x.n++; m.set(d.usuario_id, x); }
    return [...m.values()].sort((a, b) => b.total - a.total);
  }, [lista]);

  async function acao(d: Despesa, rota: string, corpo: Record<string, unknown> = {}) {
    setErro(null);
    try { await api(`/despesas/${d.id}/${rota}`, { metodo: 'POST', corpo: { versao: d.versao, ...corpo } }); setRecusar(null); setMotivo(''); carregar(); }
    catch (e) { setErro(e instanceof Error ? e.message : 'Não foi possível concluir.'); }
  }

  return (
    <>
      <div className="cabecalho-pagina"><div><span className="kicker">Financeiro</span><h1 className="titulo">Despesas de campo</h1></div></div>
      <div className="barra-filtros">
        <div className="segmentos" role="group" aria-label="Situação">
          {FILTROS.map(([v, r]) => <button key={v} className={filtro === v ? 'ativo' : ''} onClick={() => { const p = new URLSearchParams(params); p.set('status', v); setParams(p); }}>{r}</button>)}
        </div>
      </div>
      {erro && <p className="alerta" role="alert">{erro}</p>}
      {lista && lista.length > 0 && (
        <p className="nota">{lista.length} despesa{lista.length === 1 ? '' : 's'} · {real(lista.reduce((a, d) => a + Number(d.valor), 0))}
          {porTecnico.length > 1 && ` — ${porTecnico.map((t) => `${t.nome.split(' ')[0]} ${real(t.total)}`).join(', ')}`}</p>
      )}
      {lista && lista.length === 0 && <div className="cartao vazio"><div className="icone-tile"><Wallet size={24} strokeWidth={1.7} aria-hidden /></div><p>Nenhuma despesa com esse filtro.</p></div>}
      {lista && lista.length > 0 && (
        <div className="cartao tabela-cartao rolagem-x">
          <table className="tabela">
            <thead><tr><th>Data</th><th>Técnico</th><th>Tipo</th><th className="num">Valor</th><th>Comprovante</th><th>Situação</th>{podeAprovar && <th></th>}</tr></thead>
            <tbody>
              {lista.map((d) => (
                <tr key={d.id}>
                  <td>{dataBr(d.em)}</td>
                  <td>{d.usuario}<small>{d.placa ?? ''}</small></td>
                  <td>{NOME_TIPO[d.tipo]}<small>{[d.descricao, d.os_numero ? `OS ${d.os_numero}` : null, d.km != null ? `${d.km} km` : null].filter(Boolean).join(' · ')}</small></td>
                  <td className="num"><strong>{real(d.valor)}</strong></td>
                  <td>{d.tem_comprovante ? <button className="btn-link" onClick={() => setFoto(d)}><Receipt size={14} aria-hidden /> ver</button> : <span className="situacao s-nok">sem foto</span>}</td>
                  <td><span className={`situacao ${NOME_STATUS[d.status]![1]}`}>{NOME_STATUS[d.status]![0]}</span>
                    {d.motivo_recusa && <small>{d.motivo_recusa}</small>}{d.decidido_por && <small>por {d.decidido_por}</small>}</td>
                  {podeAprovar && (
                    <td className="acoes-linha">
                      {d.status === 'lancada' && d.usuario_id !== usuario?.usuarioId && <>
                        <button className="btn-link" onClick={() => acao(d, 'aprovar')}><Check size={14} aria-hidden /> aprovar</button>
                        <button className="btn-link" onClick={() => setRecusar(d)}><X size={14} aria-hidden /> recusar</button>
                      </>}
                      {d.status === 'aprovada' && <button className="btn-link" onClick={() => acao(d, 'reembolsar')}>marcar reembolsada</button>}
                      {(d.status === 'aprovada' || d.status === 'recusada') && <button className="btn-link" onClick={() => acao(d, 'reabrir')}><RotateCcw size={14} aria-hidden /> reabrir</button>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Modal titulo={foto ? `Comprovante · ${foto.usuario} · ${real(foto.valor)}` : ''} aberto={Boolean(foto)} aoFechar={() => setFoto(null)} classe="modal-largo">
        {foto && <img src={`/api/despesas/${foto.id}/comprovante`} alt="Comprovante da despesa" style={{ maxWidth: '100%', maxHeight: '75vh', display: 'block', margin: '0 auto', borderRadius: 12 }} />}
      </Modal>
      <Modal titulo="Recusar despesa" aberto={Boolean(recusar)} aoFechar={() => setRecusar(null)}>
        {recusar && (
          <div className="form">
            <p className="nota">{recusar.usuario} · {NOME_TIPO[recusar.tipo]} · {real(recusar.valor)}</p>
            <label>Motivo (o técnico vê no app)<textarea rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder='Ex.: "Comprovante ilegível", "Sem OS no dia"' /></label>
            <div className="acoes-form"><button className="btn-fantasma" onClick={() => setRecusar(null)}>Voltar</button>
              <button className="btn" disabled={motivo.trim().length < 3} onClick={() => acao(recusar, 'recusar', { motivo: motivo.trim() })}>Recusar</button></div>
          </div>
        )}
      </Modal>
    </>
  );
}
